import React, { useContext, useEffect, useRef } from 'react';
import { autoCreateQuery as autoCreatePerson, client as personClient } from '../../common/components/person/person';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS, METADATA_NAMES } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormWidget from '../../common/form/FormWidget';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';
import I18n from '../../common/i18n/I18n';
import { getPersonOption, quickAddPerson } from '../../person/helpers/QuickAddPerson';
import PersonOptionRenderer from '../../shortlists/form/PersonOptionRenderer';
import { RESOURCE_FIELD_NAMES, RESOURCE_STATUS, RESOURCE_TYPE } from './resourceConstants';

export default function ResourceCommonFormItems({
    hiddenFields = {},
    disabledFields = {}
}: {
    hiddenFields?: Record<string, boolean>;
    disabledFields?: Record<string, boolean>;
}) {
    const { state, handleChange } = useContext(FormContext);
    const lastAutoFilledNameRef = useRef<string | undefined>(undefined);
    const [savePerson] = useMutationWithContext(autoCreatePerson, personClient);
    const isCastOrCrew = [RESOURCE_TYPE.cast, RESOURCE_TYPE.crew].includes(state.type);
    const isHiredCandidateFieldVisible = !isCastOrCrew || hiddenFields[RESOURCE_FIELD_NAMES.hiredPerson];

    useEffect(() => {
        const autoValueForName = state?.role || state?.subType;
        if (autoValueForName) {
            const currentName = state.name;
            // can update name in case name is undefined
            // or name is same as last auto filled value
            const isCurrentNamePresentOrSame = !currentName || currentName === lastAutoFilledNameRef.current;

            if (isCurrentNamePresentOrSame && currentName !== autoValueForName) {
                handleChange(RESOURCE_FIELD_NAMES.name, autoValueForName);
                // cannot update name in case it is overriden by user intentionally, hence, maintaining lastAutoFilledNameRef as a ref
                lastAutoFilledNameRef.current = autoValueForName;
            }

            // Always update ref to match the latest autoValue,
            // only if the current name matches what we just auto-filled
            if (currentName === autoValueForName) {
                lastAutoFilledNameRef.current = autoValueForName;
            }
        }
    }, [state?.subType, state?.role]);

    return (
        <FormContainer>
            <FormItem hidden={hiddenFields[RESOURCE_FIELD_NAMES.type]}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                    metadataName={METADATA_NAMES.resourceType}
                    name={RESOURCE_FIELD_NAMES.type}
                    label={<I18n token="form.resource.type" />}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.type]}
                />
            </FormItem>
            <FormItem half hidden={hiddenFields[RESOURCE_FIELD_NAMES.role]}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                    metadataName={METADATA_NAMES.role}
                    name={RESOURCE_FIELD_NAMES.role}
                    label={<I18n token="form.resource.role" />}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.role]}
                />
            </FormItem>
            <FormItem half hidden={hiddenFields[RESOURCE_FIELD_NAMES.subType]}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                    metadataName={METADATA_NAMES.catalogSubType}
                    name={RESOURCE_FIELD_NAMES.subType}
                    label={<I18n token="form.resource.subtype" />}
                    dependentFieldValue={null}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.subType]}
                />
            </FormItem>
            <FormItem half hidden={hiddenFields[RESOURCE_FIELD_NAMES.resourceCategory]}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                    metadataName={METADATA_NAMES.category}
                    name={RESOURCE_FIELD_NAMES.resourceCategory}
                    label={<I18n token="form.resource.category" />}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.resourceCategory]}
                />
            </FormItem>
            <FormItem half hidden={hiddenFields[RESOURCE_FIELD_NAMES.name]}>
                <FormWidget
                    name={RESOURCE_FIELD_NAMES.name}
                    label={<I18n token="common.label" />}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.name]}
                />
            </FormItem>
            <FormItem half hidden={hiddenFields[RESOURCE_FIELD_NAMES.budgetCategory]}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                    metadataName={METADATA_NAMES.budgetCategory}
                    name={RESOURCE_FIELD_NAMES.budgetCategory}
                    label={<I18n token="form.resource.budgetCategory" />}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.budgetCategory]}
                />
            </FormItem>
            <FormItem half hidden={hiddenFields[RESOURCE_FIELD_NAMES.status]}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.autocomplete}
                    name={RESOURCE_FIELD_NAMES.status}
                    label={<I18n token="form.resource.status" />}
                    options={RESOURCE_STATUS}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.status]}
                />
            </FormItem>
            <FormItem half hidden={isHiredCandidateFieldVisible}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                    entity={ENTITY_CONSTANTS.person}
                    name={RESOURCE_FIELD_NAMES.hiredPerson}
                    label={<I18n token="form.resource.hiredCandidate" />}
                    addOption
                    OptionRenderer={PersonOptionRenderer}
                    getOption={getPersonOption}
                    onCreateHandle={quickAddPerson}
                    saveMutation={savePerson}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.hiredPerson]}
                />
            </FormItem>
            <FormItem hidden={hiddenFields[RESOURCE_FIELD_NAMES.locations]}>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.location}
                    name={RESOURCE_FIELD_NAMES.locations}
                    label={<I18n token="form.resource.locations" />}
                    multiple
                    limitTags={1}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.locations]}
                />
            </FormItem>
            <FormItem hidden={hiddenFields[RESOURCE_FIELD_NAMES.summaryAbout]}>
                <FormWidget
                    component={'TextArea'}
                    name={RESOURCE_FIELD_NAMES.summaryAbout}
                    label={<I18n token="form.resource.summary.about" />}
                    disabled={disabledFields[RESOURCE_FIELD_NAMES.summaryAbout]}
                />
            </FormItem>
        </FormContainer>
    );
}
